import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { trainerAssociationService } from '../services/trainerAssociationService';
import type { TrainerAssociation } from '../types/trainerAssociation';
import spottoLogo from '../assets/spotto-logo.png';

type PermissionOption = 'employee' | 'ownTrainings' | 'both';

export const TrainerAssociationConfirm: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { t } = useTranslation();

  const [association, setAssociation] = useState<TrainerAssociation | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form state
  const [selectedPermission, setSelectedPermission] = useState<PermissionOption>('both');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    if (!token) {
      setError(t('trainerAssociation.invalidLink'));
      setIsLoading(false);
      return;
    }

    loadAssociation();
  }, [token, t]);

  const loadAssociation = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await trainerAssociationService.getAssociationByToken(token!);
      setAssociation(data);

      if (data.status !== 'Pending') {
        setError(t('trainerAssociation.alreadyProcessed'));
      }
    } catch (err) {
      console.error('Failed to load association:', err);
      setError(t('trainerAssociation.invalidOrExpiredLink'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!token || !association) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const canRunOwnTrainings = selectedPermission === 'ownTrainings' || selectedPermission === 'both';
      const isEmployee = selectedPermission === 'employee' || selectedPermission === 'both';

      await trainerAssociationService.confirmAssociation({
        token,
        confirm: true,
        canRunOwnTrainings,
        isEmployee,
      });

      setSuccess(t('trainerAssociation.confirmSuccess'));
    } catch (err) {
      console.error('Failed to confirm association:', err);
      setError(err instanceof Error ? err.message : t('trainerAssociation.confirmError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!token || !association) return;

    setIsSubmitting(true);
    setError(null);

    try {
      await trainerAssociationService.confirmAssociation({
        token,
        confirm: false,
        rejectionReason: rejectionReason.trim() || undefined,
      });

      setSuccess(t('trainerAssociation.rejectSuccess'));
    } catch (err) {
      console.error('Failed to reject association:', err);
      setError(err instanceof Error ? err.message : t('trainerAssociation.rejectError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-surface-app flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin w-10 h-10 border-2 border-brand-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-ink-secondary">{t('trainerAssociation.loading')}</p>
        </div>
      </div>
    );
  }

  // Error state (no association loaded)
  if (error && !association) {
    return (
      <div className="min-h-screen bg-surface-app flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <img src={spottoLogo} alt="Spotto Logo" className="h-16 w-auto mx-auto mb-4" />
            <h1 className="text-3xl font-bold text-white mb-2">{t('app.name')}</h1>
          </div>

          <div className="bg-surface-card rounded-2xl border border-border-subtle p-8 shadow-xl">
            <div className="text-center">
              <div className="text-6xl mb-4">😕</div>
              <h2 className="text-xl font-bold text-white mb-2">{t('trainerAssociation.errorTitle')}</h2>
              <p className="text-brand-warm-deep">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-surface-app flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <img src={spottoLogo} alt="Spotto Logo" className="h-16 w-auto mx-auto mb-4" />
            <h1 className="text-3xl font-bold text-white mb-2">{t('app.name')}</h1>
          </div>

          <div className="bg-surface-card rounded-2xl border border-border-subtle p-8 shadow-xl">
            <div className="text-center">
              <div className="w-16 h-16 bg-brand-confirm/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-brand-confirm-deep" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-white mb-2">{t('trainerAssociation.successTitle')}</h2>
              <p className="text-brand-confirm-deep">{success}</p>
              <p className="text-ink-secondary text-sm mt-4">{t('trainerAssociation.canCloseWindow')}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-app flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Header */}
        <div className="text-center mb-8">
          <img src={spottoLogo} alt="Spotto Logo" className="h-16 w-auto mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-white mb-2">{t('app.name')}</h1>
          <p className="text-ink-secondary">SCHEDULE - SPORT - PROGRESS</p>
        </div>

        {/* Main Card */}
        <div className="bg-surface-card rounded-2xl border border-border-subtle p-8 shadow-xl">
          <h2 className="text-2xl font-bold text-white mb-2">{t('trainerAssociation.title')}</h2>
          <p className="text-ink-secondary mb-6">{t('trainerAssociation.description')}</p>

          {/* Association Info */}
          <div className="bg-surface-app-elevated rounded-xl p-4 mb-6">
            <div className="flex items-center gap-4 mb-4">
              {association?.trainerAvatarUrl ? (
                <img
                  src={association.trainerAvatarUrl}
                  alt={association.trainerDisplayName}
                  className="w-14 h-14 rounded-full object-cover"
                />
              ) : (
                <div className="w-14 h-14 bg-brand-primary rounded-full flex items-center justify-center text-xl text-white font-bold">
                  {association?.trainerDisplayName?.charAt(0) || 'T'}
                </div>
              )}
              <div>
                <p className="text-white font-semibold">{association?.trainerDisplayName}</p>
                <p className="text-ink-secondary text-sm">{association?.trainerEmail}</p>
              </div>
            </div>

            <div className="border-t border-border-strong pt-4">
              <p className="text-ink-secondary text-sm mb-1">{t('trainerAssociation.invitedBy')}</p>
              <p className="text-white font-semibold text-lg">{association?.businessName}</p>
            </div>
          </div>

          {/* Error message */}
          {error && (
            <div className="bg-brand-warm/10 border border-brand-warm/30 rounded-lg p-4 mb-6">
              <p className="text-brand-warm-deep text-sm">{error}</p>
            </div>
          )}

          {!showRejectForm ? (
            <>
              {/* Permission Selection */}
              <div className="mb-6">
                <h3 className="text-white font-semibold mb-3">{t('trainerAssociation.selectPermissions')}</h3>
                <div className="space-y-3">
                  {/* Employee Only */}
                  <label
                    className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      selectedPermission === 'employee'
                        ? 'bg-brand-primary/15 border-brand-primary-light shadow-lg shadow-brand-primary/20'
                        : 'bg-surface-app-elevated border-border-strong hover:border-border-strong hover:bg-surface-app-elevated'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                      selectedPermission === 'employee'
                        ? 'border-brand-primary-light bg-brand-primary/50'
                        : 'border-border-strong'
                    }`}>
                      {selectedPermission === 'employee' && (
                        <div className="w-2 h-2 rounded-full bg-white"></div>
                      )}
                    </div>
                    <div>
                      <p className={`font-medium ${selectedPermission === 'employee' ? 'text-brand-primary' : 'text-white'}`}>
                        {t('trainerAssociation.employeeOnly')}
                      </p>
                      <p className="text-ink-secondary text-sm">{t('trainerAssociation.employeeOnlyDesc')}</p>
                    </div>
                    <input
                      type="radio"
                      name="permission"
                      value="employee"
                      checked={selectedPermission === 'employee'}
                      onChange={() => setSelectedPermission('employee')}
                      className="sr-only"
                    />
                  </label>

                  {/* Own Trainings Only */}
                  <label
                    className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      selectedPermission === 'ownTrainings'
                        ? 'bg-brand-primary/15 border-brand-primary-light shadow-lg shadow-brand-primary/20'
                        : 'bg-surface-app-elevated border-border-strong hover:border-border-strong hover:bg-surface-app-elevated'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                      selectedPermission === 'ownTrainings'
                        ? 'border-brand-primary-light bg-brand-primary/50'
                        : 'border-border-strong'
                    }`}>
                      {selectedPermission === 'ownTrainings' && (
                        <div className="w-2 h-2 rounded-full bg-white"></div>
                      )}
                    </div>
                    <div>
                      <p className={`font-medium ${selectedPermission === 'ownTrainings' ? 'text-brand-primary' : 'text-white'}`}>
                        {t('trainerAssociation.ownTrainingsOnly')}
                      </p>
                      <p className="text-ink-secondary text-sm">{t('trainerAssociation.ownTrainingsOnlyDesc')}</p>
                    </div>
                    <input
                      type="radio"
                      name="permission"
                      value="ownTrainings"
                      checked={selectedPermission === 'ownTrainings'}
                      onChange={() => setSelectedPermission('ownTrainings')}
                      className="sr-only"
                    />
                  </label>

                  {/* Both Permissions */}
                  <label
                    className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      selectedPermission === 'both'
                        ? 'bg-brand-primary/15 border-brand-primary-light shadow-lg shadow-brand-primary/20'
                        : 'bg-surface-app-elevated border-border-strong hover:border-border-strong hover:bg-surface-app-elevated'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                      selectedPermission === 'both'
                        ? 'border-brand-primary-light bg-brand-primary/50'
                        : 'border-border-strong'
                    }`}>
                      {selectedPermission === 'both' && (
                        <div className="w-2 h-2 rounded-full bg-white"></div>
                      )}
                    </div>
                    <div>
                      <p className={`font-medium ${selectedPermission === 'both' ? 'text-brand-primary' : 'text-white'}`}>
                        {t('trainerAssociation.bothPermissions')}
                      </p>
                      <p className="text-ink-secondary text-sm">{t('trainerAssociation.bothPermissionsDesc')}</p>
                    </div>
                    <input
                      type="radio"
                      name="permission"
                      value="both"
                      checked={selectedPermission === 'both'}
                      onChange={() => setSelectedPermission('both')}
                      className="sr-only"
                    />
                  </label>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col gap-3">
                <button
                  onClick={handleConfirm}
                  disabled={isSubmitting || association?.status !== 'Pending'}
                  className="w-full bg-brand-primary text-white py-3 px-6 rounded-xl font-semibold hover:bg-brand-primary-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isSubmitting ? t('trainerAssociation.confirming') : t('trainerAssociation.confirmButton')}
                </button>

                <button
                  onClick={() => setShowRejectForm(true)}
                  disabled={isSubmitting || association?.status !== 'Pending'}
                  className="w-full bg-surface-app-elevated text-ink-primary py-3 px-6 rounded-xl font-semibold hover:bg-surface-card-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {t('trainerAssociation.rejectButton')}
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Reject Form */}
              <div className="mb-6">
                <h3 className="text-white font-semibold mb-3">{t('trainerAssociation.rejectTitle')}</h3>
                <p className="text-ink-secondary text-sm mb-4">{t('trainerAssociation.rejectDescription')}</p>

                <div>
                  <label className="block text-sm font-medium text-ink-primary mb-2">
                    {t('trainerAssociation.rejectionReason')}
                  </label>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    className="w-full px-4 py-3 bg-surface-app-elevated border border-border-strong rounded-xl text-ink-primary placeholder-ink-subtle focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                    placeholder={t('trainerAssociation.rejectionReasonPlaceholder')}
                    rows={3}
                  />
                </div>
              </div>

              {/* Reject Actions */}
              <div className="flex flex-col gap-3">
                <button
                  onClick={handleReject}
                  disabled={isSubmitting}
                  className="w-full bg-brand-warm text-white py-3 px-6 rounded-xl font-semibold hover:bg-brand-warm-deep disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isSubmitting ? t('trainerAssociation.rejecting') : t('trainerAssociation.confirmReject')}
                </button>

                <button
                  onClick={() => {
                    setShowRejectForm(false);
                    setRejectionReason('');
                  }}
                  disabled={isSubmitting}
                  className="w-full bg-surface-app-elevated text-ink-primary py-3 px-6 rounded-xl font-semibold hover:bg-surface-card-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {t('trainerAssociation.goBack')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
